/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import MenuItem from '@material-ui/core/MenuItem';
import Select, { SelectProps } from '@material-ui/core/Select';
import { Duration } from '../../types';
import { findAlways } from '../../utils/assert';
import { useSelectStyles as useStyles } from '../../utils/styles';

export type PeriodOption = {
  value: Duration;
  label: string;
};

export function getDefaultOptions(
): PeriodOption[] {
  return [
    {
      value: Duration.P7D,
      label: 'Últimas 2 semanas'
    },
    {
      value: Duration.P15D,
      label: 'Último mês'
    },
    {
      value: Duration.P30D,
      label: 'Últimos 2 meses'
    },
    {
      value: Duration.P90D,
      label: 'Últimos 6 meses',
    }
  ];
}

type PeriodSelectProps = {
  duration: Duration;
  onSelect: (duration: Duration) => void;
  options?: PeriodOption[];
};

export const PeriodSelect = ({
  duration,
  onSelect,
  options,
}: PeriodSelectProps) => {
  const classes = useStyles();
  const optionsOrDefault =
    options ?? getDefaultOptions();

  const handleOnChange: SelectProps['onChange'] = e => {
    onSelect(e.target.value as Duration);
  };

  const renderValue: SelectProps['renderValue'] = value => {
    const option = findAlways(optionsOrDefault, o => o.value === value);
    return <b>{option.label}</b>;
  };

  return (
    <Select
      className={classes.select}
      variant="outlined"
      onChange={handleOnChange}
      value={duration}
      renderValue={renderValue}
      data-testid="period-select"
    >
      {optionsOrDefault.map(option => (
        <MenuItem
          className={classes.menuItem}
          key={option.value}
          value={option.value}
          data-testid={`period-select-option-${option.value}`}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};
